"use client"

import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import {
  FaHome,
  FaWallet,
  FaHeadset,
  FaPowerOff,
  FaUserCog,
  FaBuilding,
  FaUsers,
  FaLayerGroup,
  FaCubes,
  FaChevronRight,
  // FaTasks,
  // FaTools,
} from "react-icons/fa"
import logo from "../Assets/logo.png"

function Sidebar({ isOpen, toggleSidebar }) {
  const [activeItem, setActiveItem] = useState("")
  const navigate = useNavigate()
  const location = useLocation()

  const user = JSON.parse(localStorage.getItem("user"))
  const role = user ? user.role : null

  useEffect(() => {
    const path = location.pathname

    if (path === "/dashboard") {
      setActiveItem("dashboard")
    } else if (path === "/project-tracking") {
      setActiveItem("project-tracking")
    } else if (path === "/maintenance") {
      setActiveItem("maintenance")
    } else if (path === "/billing-reports") {
      setActiveItem("billing")
    } else if (path === "/resources-monitoring") {
      setActiveItem("monitoring")
    } else if (path === "/support-request") {
      setActiveItem("supportRequest")
    } else if (path === "/ai-analysis") {
      setActiveItem("aiAnalysis")
    } else if (path.startsWith("/customer-management") || path.startsWith("/customer-detail")) {
      setActiveItem("customer-management")
    } else if (path === "/group-management" || path.startsWith("/groups")) {
      setActiveItem("group-management")
    } else if (path === "/user-management") {
      setActiveItem("user-management")
    } else if (path === "/project-management") {
      setActiveItem("project-management")
    } else if (path === "/service-catalog") {
      setActiveItem("service-catalog")
    } else if (path === "/settings") {
      setActiveItem("settings-general")
    } else if (path === "/user-information") {
      setActiveItem("user-information")
    } else {
      setActiveItem("")
    }
  }, [location.pathname])

  const handleClick = (item, path) => {
    setActiveItem(item)
    navigate(path)
    if (typeof window !== "undefined" && window.innerWidth < 1024 && toggleSidebar) {
      toggleSidebar()
    }
  }

  const handleLogout = (e) => {
    e.preventDefault()
    navigate("/login")
  }

  const MenuItem = ({ item, path, icon: Icon, children, isActive, badge }) => (
    <li
      className={`group relative mb-1 transition-all duration-200 ${
        isActive
          ? "text-white shadow-lg"
          : "text-gray-600 hover:text-[#0f4d97] hover:bg-blue-50/80 cursor-pointer"
      } rounded-xl`}
      style={{
        background: isActive ? "#0f4d97" : "transparent",
        boxShadow: isActive ? "0 10px 25px -5px rgba(15, 77, 151, 0.25)" : "none"
      }}
      onClick={() => handleClick(item, path)}
    >
      <div className="flex items-center justify-between p-3 px-4">
        <div className="flex items-center">
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 transition-all duration-200 ${
              isActive
                ? "bg-[#ed2a62] text-white"
                : "bg-gray-100 text-gray-500 group-hover:bg-blue-100 group-hover:text-[#0f4d97]"
            }`}
          >
            <Icon className="w-4 h-4" />
          </div>
          <span className="font-medium text-sm">{children}</span>
        </div>
        {badge && <span className="bg-[#ed2a62] text-white text-xs px-2 py-1 rounded-full">{badge}</span>}
        {isActive && <FaChevronRight className="w-3 h-3 text-white/70" />}
      </div>
    </li>
  )

  const SectionHeader = ({ children }) => (
    <div className="px-4 mb-3">
      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{children}</h3>
    </div>
  )

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-900/40 backdrop-blur-xs z-40 lg:hidden"
          onClick={() => {
            if (toggleSidebar) toggleSidebar()
          }}
          aria-hidden="true"
        />
      )}
      <div
        className={`h-screen bg-gradient-to-b from-gray-50 to-white border-r border-gray-200/60 flex flex-col fixed top-0 left-0 transition-all duration-300 overflow-hidden z-50 ${
          isOpen ? "w-64 shadow-xl shadow-gray-900/10" : "w-0"
        }`}
        style={{ width: isOpen ? "16rem" : "0" }}
      >
      <div className="flex flex-col h-full w-64">
        {/* Logo Section */}
        <div className="flex items-center justify-center h-20 px-4 border-b border-gray-200/60 overflow-hidden">
          <div
            className="cursor-pointer transition-transform duration-200 hover:scale-105 flex items-center justify-center"
            onClick={() => navigate("/dashboard")}
          >
            <img
              src={logo || "/placeholder.svg"}
              alt="MagnaSight Logo"
              className="w-44 h-44 object-contain"
            />
          </div>
        </div>

        {/* Navigation Content */}
        <div className="flex-1 overflow-y-auto py-6 px-3">
          <div className="space-y-6">
            {/* Services Section */}
            <div>
              <SectionHeader>Services</SectionHeader>
              <ul className="space-y-1">
                <MenuItem item="dashboard" path="/dashboard" icon={FaHome} isActive={activeItem === "dashboard"}>
                  Dashboard
                </MenuItem>
                {/* Hidden in production release
                <MenuItem item="project-tracking" path="/project-tracking" icon={FaTasks} isActive={activeItem === "project-tracking"}>
                  Project Tracking
                </MenuItem>
                <MenuItem item="maintenance" path="/maintenance" icon={FaTools} isActive={activeItem === "maintenance"}>
                  Maintenance Hub
                </MenuItem>
                */}
                <MenuItem item="billing" path="/billing-reports" icon={FaWallet} isActive={activeItem === "billing"}>
                  Cloud Billing (GCP)
                </MenuItem>
                <MenuItem
                  item="supportRequest"
                  path="/support-request"
                  icon={FaHeadset}
                  isActive={activeItem === "supportRequest"}
                >
                  Support Request
                </MenuItem>
              </ul>
            </div>

            {/* Management Section */}
            {role !== "Customer" && (
              <div>
                <SectionHeader>Management</SectionHeader>
                <ul className="space-y-1">
                  {role !== "Customer Admin" && (
                    <MenuItem
                      item="customer-management"
                      path="/customer-management"
                      icon={FaBuilding}
                      isActive={activeItem === "customer-management"}
                    >
                      Customers
                    </MenuItem>
                  )}
                  <MenuItem
                    item="user-management"
                    path="/user-management"
                    icon={FaUserCog}
                    isActive={activeItem === "user-management"}
                  >
                    Users
                  </MenuItem>
                  <MenuItem
                    item="group-management"
                    path="/group-management"
                    icon={FaUsers}
                    isActive={activeItem === "group-management"}
                  >
                    Groups
                  </MenuItem>
                  <MenuItem
                    item="project-management"
                    path="/project-management"
                    icon={FaLayerGroup}
                    isActive={activeItem === "project-management"}
                  >
                    GCP Project Access
                  </MenuItem>
                  {role === "Admin" && (
                    <MenuItem
                      item="service-catalog"
                      path="/service-catalog"
                      icon={FaCubes}
                      isActive={activeItem === "service-catalog"}
                    >
                      Service Catalog
                    </MenuItem>
                  )}
                </ul>
              </div>
            )}

            {/* Settings Section */}
            <div>
              <SectionHeader>Account</SectionHeader>
              <ul className="space-y-1">
                <MenuItem
                  item="user-information"
                  path="/user-information"
                  icon={FaUserCog}
                  isActive={activeItem === "user-information"}
                >
                  User Information
                </MenuItem>
              </ul>
            </div>
          </div>
        </div>

        {/* Logout Section */}
        <div className="border-t border-gray-200/60 p-4">
          <button
            className="w-full flex items-center p-3 text-gray-500 hover:text-[#ed2a62] hover:bg-red-50 rounded-xl transition-all duration-200 group"
            onClick={handleLogout}
          >
            <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 bg-gray-100 text-gray-500 group-hover:bg-red-100 group-hover:text-[#ed2a62] transition-all duration-200">
              <FaPowerOff className="w-4 h-4" />
            </div>
            <span className="font-medium text-sm">Log Out</span>
          </button>
        </div>
      </div>
    </div>
    </>
  )
}

export default Sidebar
